<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter :filter-title="$t('pim.employee_information')">
      <oxd-form @submit-valid="filterItems" @reset="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.employee"
                :rules="rules.employee"
                :params="{includeEmployees: filters.includeEmployees?.param}"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.employeeId"
                :label="$t('general.employee_id')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <employment-status-dropdown v-model="filters.empStatusId" />
            </oxd-grid-item>
            <oxd-grid-item>
              <include-employee-dropdown
                v-model="filters.includeEmployees"
              ></include-employee-dropdown>
            </oxd-grid-item>
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.supervisor"
                :rules="rules.supervisor"
                :label="$t('pim.supervisor_name')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <jobtitle-dropdown v-model="filters.jobTitleId" />
            </oxd-grid-item>
            <oxd-grid-item>
              <subunit-dropdown v-model="filters.subunitId" />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            type="reset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="orangehrm-paper-container">
      <div
        v-if="$can.create('employee_list')"
        class="orangehrm-header-container"
      >
        <oxd-button
          :label="$t('general.add')"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
      <div class="aqe-grid-wrap">
        <data-grid
          ref="grid"
          :columns="gridColumns"
          :rows="rows"
          :total="total"
          :page="page"
          :page-size="pageSize"
          :loading="isLoading"
          :sort="sort"
          :selectable="$can.delete('employee_list')"
          :can-delete="$can.delete('employee_list')"
          :expandable="true"
          :context-actions="contextActions"
          storage-key="pim-employees"
          label-key="fullName"
          class="orangehrm-employee-list"
          @sort="onGridSort"
          @page="onGridPage"
          @page-size="onGridPageSize"
          @open="onClickEdit"
          @edit="onInlineEdit"
          @delete="onGridDelete"
          @context="onContext"
          @export="onExportAll"
          @expand="loadDetail"
        >
          <template #cell-fullName="{row}">
            <span class="aqe-emp">
              <span
                class="aqe-emp__avatar"
                :data-hue="hue(row)"
                aria-hidden="true"
                >{{ initials(row) }}</span
              >
              <span class="aqe-emp__name">{{ row.fullName }}</span>
              <span v-if="row.terminated" class="aqe-emp__tag">Past</span>
            </span>
          </template>
          <template #cell-empStatus="{row}">
            <span
              v-if="row.empStatus"
              class="aqe-pill"
              :data-tone="tone(row.empStatus)"
              >{{ row.empStatus }}</span
            >
          </template>
          <template #actions="{row}">
            <oxd-icon-button
              name="pencil-fill"
              :title="'Edit'"
              @click="onClickEdit(row)"
            />
            <oxd-icon-button
              v-if="$can.delete('employee_list') && row.isSelectable"
              name="trash"
              :title="'Delete'"
              @click="onClickDelete(row)"
            />
          </template>
          <template #detail="{row}">
            <div class="aqe-detail">
              <div v-if="!details[row.id]" class="aqe-detail__loading">
                <span class="aqe-detail__spin"></span> Loading details…
              </div>
              <template v-else>
                <dl>
                  <div>
                    <dt>Employee ID</dt>
                    <dd>{{ row.employeeId || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Date of birth</dt>
                    <dd>{{ details[row.id].birthday || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Gender</dt>
                    <dd>{{ details[row.id].gender || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Nationality</dt>
                    <dd>{{ details[row.id].nationality || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Marital status</dt>
                    <dd>{{ details[row.id].maritalStatus || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Supervisor</dt>
                    <dd>{{ row.supervisor || '—' }}</dd>
                  </div>
                </dl>
                <div class="aqe-detail__links">
                  <a :href="link('/pim/viewPersonalDetails/empNumber/', row.id)"
                    >Personal</a
                  >
                  <a :href="link('/pim/contactDetails/empNumber/', row.id)"
                    >Contact</a
                  >
                  <a :href="link('/pim/viewJobDetails/empNumber/', row.id)"
                    >Job</a
                  >
                  <a :href="link('/pim/viewSalaryList/empNumber/', row.id)"
                    >Salary</a
                  >
                </div>
              </template>
            </div>
          </template>
        </data-grid>
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {ref} from 'vue';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import JobtitleDropdown from '@/orangehrmPimPlugin/components/JobtitleDropdown';
import SubunitDropdown from '@/orangehrmPimPlugin/components/SubunitDropdown';
import EmploymentStatusDropdown from '@/orangehrmPimPlugin/components/EmploymentStatusDropdown';
import IncludeEmployeeDropdown from '@/core/components/dropdown/IncludeEmployeeDropdown';
import DataGrid from '@/core/components/table/DataGrid.vue';
import {
  shouldNotExceedCharLength,
  validSelection,
} from '@/core/util/validation/rules';
import usei18n from '@/core/util/composable/usei18n';

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'employee-autocomplete': EmployeeAutocomplete,
    'jobtitle-dropdown': JobtitleDropdown,
    'subunit-dropdown': SubunitDropdown,
    'employment-status-dropdown': EmploymentStatusDropdown,
    'include-employee-dropdown': IncludeEmployeeDropdown,
    'data-grid': DataGrid,
  },

  props: {
    unselectableEmpNumbers: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const {$t} = usei18n();
    const filters = ref({
      employee: null,
      employeeId: '',
      empStatusId: null,
      supervisor: null,
      jobTitleId: null,
      subunitId: null,
      includeEmployees: {
        id: 1,
        param: 'onlyCurrent',
        label: $t('general.current_employees_only'),
      },
    });
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/employees',
    );
    return {http, filters};
  },

  data() {
    let pageSize = 25;
    try {
      pageSize = Number(localStorage.getItem('aqe.pim.pageSize')) || 25;
    } catch (e) {
      /* storage unavailable */
    }
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize,
      isLoading: false,
      sort: {field: 'employee.firstName', order: 'ASC'},
      details: {},
      requestSeq: 0,
      rules: {
        employee: [shouldNotExceedCharLength(100)],
        supervisor: [shouldNotExceedCharLength(100), validSelection],
      },
    };
  },

  computed: {
    gridColumns() {
      return [
        {
          key: 'employeeId',
          title: this.$t('general.id'),
          sortField: 'employee.employeeId',
          width: 100,
        },
        {
          key: 'fullName',
          title: 'Employee',
          sortField: 'employee.firstName',
          width: 230,
        },
        {
          key: 'firstName',
          title: this.$t('general.first_name'),
          sortField: 'employee.firstName',
          width: 150,
          editable: true,
          hidden: true,
        },
        {
          key: 'lastName',
          title: this.$t('general.last_name'),
          sortField: 'employee.lastName',
          width: 140,
          editable: true,
        },
        {
          key: 'jobTitle',
          title: this.$t('general.job_title'),
          sortField: 'jobTitle.jobTitleName',
          width: 180,
        },
        {
          key: 'empStatus',
          title: this.$t('general.employment_status'),
          sortField: 'empStatus.name',
          width: 175,
        },
        {
          key: 'subunit',
          title: this.$t('general.sub_unit'),
          sortField: 'subunit.name',
          width: 150,
        },
        {
          key: 'supervisor',
          title: this.$t('pim.supervisor'),
          sortField: 'supervisor.firstName',
          width: 160,
        },
        {key: 'location', title: 'Location', width: 150, hidden: true},
      ];
    },
    contextActions() {
      return [
        {
          key: 'open',
          label: 'Open profile',
          icon: 'person-lines-fill',
          kbd: 'Enter',
        },
        {key: 'open-tab', label: 'Open in new tab', icon: 'box-arrow-up-right'},
        {key: 'job', label: 'Job details', icon: 'briefcase'},
        {
          key: 'edit-inline',
          label: 'Rename',
          icon: 'input-cursor-text',
          kbd: 'F2',
        },
        {
          key: 'copy-id',
          label: 'Copy employee ID',
          icon: 'clipboard',
          disabled: (r) => !r.employeeId,
        },
        {
          key: 'delete',
          label: 'Delete',
          icon: 'trash3',
          danger: true,
          disabled: (r) =>
            !this.$can.delete('employee_list') || !r.isSelectable,
        },
      ];
    },
    query() {
      const f = this.filters;
      return {
        model: 'detailed',
        nameOrId: typeof f.employee === 'string' ? f.employee : undefined,
        empNumber: f.employee?.id,
        employeeId: f.employeeId || undefined,
        empStatusId: f.empStatusId?.id,
        includeEmployees: f.includeEmployees?.param,
        supervisorEmpNumbers: f.supervisor ? [f.supervisor.id] : undefined,
        jobTitleId: f.jobTitleId?.id,
        subunitId: f.subunitId?.id,
        sortField: this.sort.field,
        sortOrder: this.sort.order,
      };
    },
  },

  beforeMount() {
    this.execQuery();
  },

  methods: {
    normalize(item) {
      return {
        id: item.empNumber,
        employeeId: item.employeeId,
        firstName: item.firstName,
        middleName: item.middleName,
        lastName: item.lastName,
        fullName: [item.firstName, item.middleName, item.lastName]
          .filter(Boolean)
          .join(' '),
        terminated: !!item.terminationId,
        jobTitle: item.jobTitle?.isDeleted
          ? item.jobTitle.title + this.$t('general.deleted')
          : item.jobTitle?.title,
        empStatus: item.empStatus?.name,
        subunit: item.subunit?.name,
        location: item.location?.name,
        supervisor: (item.supervisors || [])
          .map((s) => `${s.firstName} ${s.lastName}`)
          .join(', '),
        isSelectable: !this.unselectableEmpNumbers.some(
          (n) => n == item.empNumber,
        ),
      };
    },
    async execQuery() {
      const seq = ++this.requestSeq;
      this.isLoading = true;
      try {
        const {data} = await this.http.getAll({
          ...this.query,
          limit: this.pageSize,
          offset: (this.page - 1) * this.pageSize,
        });
        if (seq !== this.requestSeq) return; // superseded by a newer request
        this.rows = (data.data || []).map(this.normalize);
        this.total = data.meta?.total ?? this.rows.length;
        if (!this.total) this.$toast.noRecordsFound();
      } finally {
        if (seq === this.requestSeq) this.isLoading = false;
      }
    },
    filterItems() {
      this.page = 1;
      return this.execQuery();
    },
    onGridSort(s) {
      this.sort = s;
      this.page = 1;
      this.execQuery();
    },
    onGridPage(n) {
      this.page = n;
      this.execQuery();
    },
    onGridPageSize(n) {
      this.pageSize = n;
      this.page = 1;
      try {
        localStorage.setItem('aqe.pim.pageSize', String(n));
      } catch (e) {
        /* storage unavailable */
      }
      this.execQuery();
    },
    initials(row) {
      return ((row.firstName || '')[0] || '') + ((row.lastName || '')[0] || '');
    },
    hue(row) {
      return String((row.id * 7) % 6);
    },
    link(path, id) {
      return `${window.appGlobal.baseUrl}${path}${id}`;
    },
    tone(status) {
      const s = String(status).toLowerCase();
      if (s.includes('full')) return 'green';
      if (s.includes('part')) return 'blue';
      if (s.includes('contract') || s.includes('freelance')) return 'amber';
      if (s.includes('intern') || s.includes('probation')) return 'violet';
      return 'slate';
    },
    async loadDetail(row) {
      if (this.details[row.id]) return;
      try {
        const {data} = await this.http.request({
          method: 'GET',
          url: `/api/v2/pim/employees/${row.id}/personal-details`,
        });
        const d = data.data || {};
        this.details = {
          ...this.details,
          [row.id]: {
            birthday: d.birthday,
            gender: d.gender === 1 ? 'Male' : d.gender === 2 ? 'Female' : null,
            nationality: d.nationality?.name,
            maritalStatus: d.maritalStatus,
          },
        };
      } catch (e) {
        this.details = {...this.details, [row.id]: {}};
      }
    },
    onClickAdd() {
      navigate('/pim/addEmployee');
    },
    onClickEdit(row) {
      navigate('/pim/viewPersonalDetails/empNumber/{id}', {id: row.id});
    },
    async onInlineEdit({row, key, value}) {
      if (!value || value.length > 30) {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message: !value
            ? this.$t('general.required')
            : this.$t('general.should_not_exceed_n_characters', {amount: 30}),
        });
        return;
      }
      const previous = row[key];
      const rebuild = () => {
        row.fullName = [row.firstName, row.middleName, row.lastName]
          .filter(Boolean)
          .join(' ');
      };
      row[key] = value; // optimistic; rolled back on failure
      rebuild();
      try {
        await this.http.request({
          method: 'PUT',
          url: `/api/v2/pim/employees/${row.id}/personal-details`,
          data: await this.personalBody(row),
        });
        this.$toast.updateSuccess();
      } catch (e) {
        row[key] = previous;
        rebuild();
      }
    },
    async personalBody(row) {
      // PUT replaces the whole record, so start from what the server has.
      const {data} = await this.http.request({
        method: 'GET',
        url: `/api/v2/pim/employees/${row.id}/personal-details`,
      });
      const d = data.data || {};
      return {
        lastName: row.lastName,
        firstName: row.firstName,
        middleName: d.middleName || '',
        employeeId: d.employeeId || '',
        otherId: d.otherId || '',
        drivingLicenseNo: d.drivingLicenseNo || '',
        drivingLicenseExpiredDate: d.drivingLicenseExpiredDate,
        gender: d.gender,
        maritalStatus: d.maritalStatus || '',
        birthday: d.birthday,
        nationalityId: d.nationality?.id ?? null,
      };
    },
    async onContext(action, row) {
      switch (action) {
        case 'open':
          return this.onClickEdit(row);
        case 'open-tab':
          window.open(
            this.link('/pim/viewPersonalDetails/empNumber/', row.id),
            '_blank',
            'noopener',
          );
          return;
        case 'job':
          return navigate('/pim/viewJobDetails/empNumber/{id}', {id: row.id});
        case 'copy-id':
          try {
            await navigator.clipboard.writeText(row.employeeId);
            this.$toast.info({
              title: this.$t('general.info'),
              message: `Copied ${row.employeeId}`,
            });
          } catch (e) {
            this.$toast.warn({
              title: this.$t('general.warning'),
              message: 'Clipboard is not available',
            });
          }
          return;
        case 'delete':
          return this.onClickDelete(row);
      }
    },
    async fetchAll() {
      const {data} = await this.http.getAll({
        ...this.query,
        limit: 0,
        offset: 0,
      });
      return data.data || [];
    },
    async onGridDelete(selection) {
      let ids = selection;
      if (!Array.isArray(selection)) {
        const skip = new Set(selection.excluded);
        ids = (await this.fetchAll())
          .map((i) => i.empNumber)
          .filter(
            (id) =>
              !skip.has(id) &&
              !this.unselectableEmpNumbers.some((n) => n == id),
          );
      }
      if (!ids.length) return;
      const confirmation = await this.$refs.deleteDialog.showDialog();
      if (confirmation === 'ok') this.deleteItems(ids);
    },
    async onClickDelete(row) {
      if (!row.isSelectable) return this.$toast.cannotDelete();
      const confirmation = await this.$refs.deleteDialog.showDialog();
      if (confirmation === 'ok') this.deleteItems([row.id]);
    },
    async deleteItems(ids) {
      this.isLoading = true;
      await this.http.deleteAll({ids});
      await this.$toast.deleteSuccess();
      this.$refs.grid?.clearSelection();
      if (this.page > 1 && ids.length >= this.rows.length) this.page -= 1;
      this.execQuery();
    },
    async onExportAll({columns}) {
      this.isLoading = true;
      try {
        const rows = (await this.fetchAll()).map(this.normalize);
        this.$refs.grid.download('csv', rows, columns);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style src="./employee.scss" lang="scss" scoped></style>

<style lang="scss" scoped>
.aqe-grid-wrap {
  padding: 0 24px 20px;
}
.aqe-emp {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
}
.aqe-emp__avatar {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  &[data-hue='1'] {
    background: #dcfce7;
    color: #166534;
  }
  &[data-hue='2'] {
    background: #fef3c7;
    color: #92400e;
  }
  &[data-hue='3'] {
    background: #fce7f3;
    color: #9d174d;
  }
  &[data-hue='4'] {
    background: #e0f2fe;
    color: #075985;
  }
  &[data-hue='5'] {
    background: #ede9fe;
    color: #5b21b6;
  }
}
.aqe-emp__name {
  overflow: hidden;
  text-overflow: ellipsis;
  color: #0f172a;
  font-weight: 600;
}
.aqe-emp__tag {
  padding: 2px 7px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
}
.aqe-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #f1f5f9;
  color: #475569;
  &[data-tone='green'] {
    background: #ecfdf5;
    color: #047857;
  }
  &[data-tone='blue'] {
    background: #eff6ff;
    color: #1d4ed8;
  }
  &[data-tone='amber'] {
    background: #fffbeb;
    color: #b45309;
  }
  &[data-tone='violet'] {
    background: #f5f3ff;
    color: #6d28d9;
  }
}
.aqe-detail {
  padding: 16px 20px 18px 104px;
  dl {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px 24px;
    margin: 0;
  }
  dt {
    font-size: 11.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
  }
  dd {
    margin: 2px 0 0;
    color: #0f172a;
    font-weight: 500;
  }
}
.aqe-detail__links {
  display: flex;
  gap: 8px;
  margin-top: 14px;
  a {
    padding: 6px 12px;
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.1);
    color: #334155;
    font-size: 12.5px;
    font-weight: 600;
    text-decoration: none;
    &:hover {
      background: #0f172a;
      color: #fff;
    }
  }
}
.aqe-detail__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
}
.aqe-detail__spin {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.12);
  border-top-color: #6366f1;
  animation: aqe-spin 0.8s linear infinite;
}
@keyframes aqe-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 800px) {
  .aqe-grid-wrap {
    padding: 0 8px 12px;
  }
  .aqe-detail {
    padding-left: 20px;
  }
}
</style>
